import { getLocalizedStringValue } from '@griply/utils';
import { addDays, endOfDay } from 'date-fns';
import React from 'react';
import { ActionPlanGoalHeader } from 'shared/components/ui/action-plan-goal-header';
import { Collapse } from 'shared/components/ui/collapse';
import { useToday } from 'shared/contexts/today';
import { useToggle } from 'shared/hooks/use-toggle';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import {
  GoalTemplateInstance,
  TaskTemplateInstance,
} from 'shared/types/goal-template';
import { LifeArea } from 'shared/types/life-area';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';

import { GoalTemplatePreviewActionPlan } from './goal-template-preview-action-plan';
import * as Styled from './goal-template-preview-action-plan.style';

type GoalProps = {
  goal: GoalTemplateInstance;

  tasks: TaskTemplateInstance[];

  lifeAreas: LifeArea[];
  allGoals: GoalTemplateInstance[];
  timeFormat: TimeFormatOptions;
};

const Goal: React.FC<GoalProps> = ({
  goal,
  tasks,
  lifeAreas,
  allGoals,
  timeFormat,
}) => {
  const locale = useI18nLocale();
  const today = useToday();

  const [collapseOpen, toggleCollapse] = useToggle();

  const goalColor =
    getLinkedItemColor(goal.id, { goals: allGoals, lifeAreas }) ?? undefined;
  return (
    <>
      <ActionPlanGoalHeader
        id={goal.id}
        name={getLocalizedStringValue(goal.name, locale)}
        iconName={goal.iconName}
        color={goalColor}
        startDate={
          typeof goal.daysTillStartDate === 'number'
            ? addDays(endOfDay(today), goal.daysTillStartDate)
            : null
        }
        deadline={
          typeof goal.daysTillDeadline === 'number'
            ? addDays(endOfDay(today), goal.daysTillDeadline)
            : null
        }
        metric={goal.metric}
        onOpen={toggleCollapse}
        isOpen={collapseOpen}
        onPremium={() => {}}
        isPreview
      />
      <Collapse isOpen={collapseOpen}>
        <Styled.SublistContainer $maximalIndentation>
          <GoalTemplatePreviewActionPlan
            tasks={tasks}
            mainGoal={goal}
            lifeAreas={lifeAreas}
            goals={allGoals}
            noRightSpacing
            timeFormat={timeFormat}
          />
        </Styled.SublistContainer>
      </Collapse>
    </>
  );
};

export type GoalListProps = {
  goals: GoalTemplateInstance[];
  tasks: TaskTemplateInstance[];
  timeFormat: TimeFormatOptions;

  noRightSpacing?: boolean;

  lifeAreas: LifeArea[];
  allGoals: GoalTemplateInstance[];
};

export const GoalList: React.FC<GoalListProps> = ({
  goals,
  tasks,
  noRightSpacing,
  timeFormat,

  lifeAreas,
  allGoals,
}) => (
  <Styled.List $noRightSpacing={!!noRightSpacing}>
    {goals.map((goal) => (
      <Styled.ListItem key={goal.id}>
        <Goal
          goal={goal}
          allGoals={allGoals}
          tasks={tasks}
          lifeAreas={lifeAreas}
          timeFormat={timeFormat}
        />
      </Styled.ListItem>
    ))}
  </Styled.List>
);
